import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS,
  CACHE_KEY_PHARMAKON_V3_ARCHIVED_MEDS,
  CACHE_KEY_PHARMAKON_V3_PENDING_TRANSFERS,
} from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import type { Home, HomeArchivedItem, HomePrescriptionItem, MedicationSearchResult } from 'src/app/types';

export const useArchivedMedicationList = (
  accountId: number,
  enabled?: boolean
): UseQueryResult<HomeArchivedItem[], AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_V3_ARCHIVED_MEDS, accountId],
    () =>
      authClient.get(`v3/archived_medications/${accountId}`).then((resp: { data: HomeArchivedItem[] }) => resp.data),
    {
      ...(enabled !== undefined && { enabled }),
    }
  );
};

export const useActivePendingMedicationList = (
  accountId: number,
  enabled?: boolean
): UseQueryResult<Home, AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS, accountId],
    () => authClient.get(`v3/active_pending_medications/${accountId}`).then((resp: { data: Home }) => resp.data),
    {
      ...(enabled !== undefined && { enabled }),
    }
  );
};

export const usePendingTransferList = (
  accountId: number,
  enabled?: boolean
): UseQueryResult<HomePrescriptionItem[], AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_V3_PENDING_TRANSFERS, accountId],
    () =>
      authClient
        .get(`home/${accountId}/pending_items?type=transfer`)
        .then((resp: { data: HomePrescriptionItem[] }) => resp.data),
    {
      ...(enabled !== undefined && { enabled }),
    }
  );
};

export enum MedicationSearchSource {
  healthProfileForm = 'healthProfile',
  transferForm = 'transfer',
}

interface MedicationSearchRequest {
  searchTerm: string;
  zipCode: string;
  source?: MedicationSearchSource;
}

export const useSearchMedication = (): UseMutationResult<
  MedicationSearchResult[],
  AxiosError,
  MedicationSearchRequest
> => {
  const authClient = useBffClient();

  return useMutation(({ searchTerm, zipCode, source }) =>
    authClient
      .get('v3/medications/search', { params: { searchTerm, zipCode, source } })
      .then((resp: { data: MedicationSearchResult[] }) => resp.data)
  );
};
